import * as React from 'react';
import { FixMeLater } from '../../utils';
import { FieldBase, FieldBaseProps } from '../FieldBase';
import { TextAreaBase, TextAreaBaseProps } from '../TextAreaBase';

export interface MultilineFieldProps
    extends Pick<
            FieldBaseProps,
            | 'fullWidth'
            | 'label'
            | 'shrinkLabel'
            | 'icon'
            | 'textAlign'
            | 'size'
            | 'variant'
            | 'showClear'
            | 'buttonIcon'
            | 'onButtonClick'
            | 'buttonRef'
            | 'width'
            | 'validationStatus'
        >,
        Omit<TextAreaBaseProps, 'error'> {
    /**
     * A reference passed to the `input` element.
     */
    inputRef?: React.Ref<HTMLTextAreaElement>;

    /**
     * A reference passed to the root element.
     */
    ref?: React.Ref<FixMeLater>;
}

/**
 * TextField component allows a user to enter and edit text into a UI. They typically appear in forms and dialogs.
 * The TextField comes in various different sizes and states. It also provides multiple field variants to select from and use.
 * Additionally, we can specify labels and icons for the text field via props to provide more information to the user.
 */

export function MultilineField(props: Readonly<MultilineFieldProps>): React.JSX.Element {
    const {
        disabled,
        fullWidth,
        id,
        inputRef,
        label,
        shrinkLabel,
        size,
        variant,
        showClear,
        onButtonClick,
        buttonRef,
        width,
        validationStatus,
        ref,
        ...other
    } = props;

    return (
        <FieldBase
            ref={ref}
            id={id}
            disabled={disabled}
            label={label}
            fullWidth={fullWidth}
            shrinkLabel={shrinkLabel}
            size={size}
            variant={variant}
            showClear={showClear}
            onButtonClick={onButtonClick}
            buttonRef={buttonRef}
            width={width}
            validationStatus={validationStatus}
        >
            <TextAreaBase
                ref={inputRef}
                isFieldBaseControl={true}
                id={id as string}
                disabled={disabled}
                error={validationStatus === 'error'}
                {...other}
            />
        </FieldBase>
    );
}
